/*global Daon */
/*eslint no-undef: "error"*/

import React, { Component } from "react";
import { connect } from "react-redux";
import { func, object, instanceOf } from "prop-types";

import { trackerLicenseFileName, CLR_UPDATE_TYPES } from "./livenessData";
import FaceCaptureUI from "../FaceCaptureUI";
import { arrayBufferToBase64 } from "../../logic/arrayBufferToBase64";
import "./style.css";
import PageContent from "../PageContent";
import {
  setVideoId,
  retry,
  firstTimeInit,
  errorMessage,
} from "../../logic/actions/livenessTest";
import { go } from "../../logic/actions/navigation";
import { challengeTemplateUpload } from "../../logic/actions/api/liveness";
import { LIVENESS_TEST_RETRY, THANK_YOU } from "../../logic/enums/pages";
import {
  logEvent,
  LIVENESS_TEST_STARTED,
  STEP_COMPLETED,
} from "../../logic/eventLogger";
import { WEB_CLR } from "../../logic/enums/livenessTestType";
import CaptureButtonPanel from "../CaptureButtonPanel";
import { CONNECTION_ISSUES } from "../../logic/enums/errorMessages";

export class PageLivenessTest extends Component {
  static propTypes = {
    retry: func,
    livenessTestData: object,
    challengeTemplateUpload: func,
    CLRCaptureInstance: instanceOf(Daon.CLRCapture),
    setVideoId: func,
    go: func,
  };
  constructor(props) {
    super(props);
    this.state = {
      isRunning: false,
      attempts: false,
      sequence: undefined,
      pending: true,
    };
    this.prepareVideo.bind(this);
    this.start.bind(this);
  }

  componentDidMount() {
    this.setState({
      sequence: JSON.parse(
        this.props.livenessTestData.items[0].additionalConfig.webClrConfig
      ),
    });
  }

  prepareVideo = (fc) => {
    const videoEl = fc.camera.videoEl;
    videoEl.onloadedmetadata = () => {
      this.setState({ cameraStarted: true, pending: false });
      this.initWebClr({
        videoEl,
        width: videoEl.videoWidth,
        height: videoEl.videoHeight,
      });
    };
  };

  initWebClr({ videoEl, width, height }) {
    this.props.CLRCaptureInstance.init({
      videoEl,
      width,
      height,
      flashDuration: 334,
      trackerLicenseFileName,
      frameProcessInterval: 50,
      onColorChange: (colorCode) => this.setState({ colorCode }),
      onUpdate: (updateType, message) => {
        if (updateType !== CLR_UPDATE_TYPES.IED && message)
          this.setState({
            message:
              "Please make sure that your face is kept within the oval and you don't have stong external light sources",
          });
        if (updateType === CLR_UPDATE_TYPES.CLR_READY) {
          this.props.firstTimeInit();
        }
      },
      onTemplateCreated: (template, qualityString) => {
        this.props.CLRCaptureInstance.stopProcessing();
        this.setState({ pending: false });
        if (qualityString !== "ok") {
          this.props.errorMessage(qualityString);
          this.props.go(LIVENESS_TEST_RETRY);
        } else {
          if (this.props.CLRCaptureInstance.checkClientFps() > 14.3) {
            this.sendTemplate(template);
          } else {
            this.props.go(LIVENESS_TEST_RETRY);
          }
        }
      },
    });
  }

  sendTemplate(template) {
    this.setState({ pending: true });
    const base64template = arrayBufferToBase64(template);
    this.props
      .challengeTemplateUpload(base64template)
      .then((response) => {
        if (response.processingStatus === "PROCESSED") {
          logEvent(STEP_COMPLETED, { value: "liveness_test" });
          logEvent(STEP_COMPLETED + "_liveness_test");
          this.props.go(THANK_YOU);
        } else {
          this.props.go(LIVENESS_TEST_RETRY);
        }
        return response;
      })
      .catch(() => {
        setTimeout(() => {
          this.props.go(LIVENESS_TEST_RETRY);
        }, 3000); // if network is slow or broken we are displaying a message to a user and navigate him to do a retry after 3 secnds
        this.setState({
          message: CONNECTION_ISSUES,
          networkError: true,
          isRunning: false,
          pending: false,
        });
      });
  }

  start = () => {
    logEvent(LIVENESS_TEST_STARTED, { value: WEB_CLR });
    logEvent(LIVENESS_TEST_STARTED + "_" + WEB_CLR.toLowerCase());
    this.setState({ isRunning: true });
    this.props.CLRCaptureInstance.start(this.state.sequence);
  };

  render() {
    const messageClass = `message-box ${
      this.state.networkError ? "message-box-error" : ""
    }`;
    const clrLoaded =
      this.state.cameraStarted && this.props.firstTimeInitialized;

    return (
      <PageContent
        type="capture"
        toggleLoading={!clrLoaded || this.state.pending}
      >
        <h1 className="capture-header">Extra checks</h1>
        <div>
          <div
            className="curtain"
            style={{
              display: this.state.isRunning ? "block" : "none",
              backgroundColor: this.state.colorCode,
            }}
          />
          <FaceCaptureUI
            onCameraStarted={this.prepareVideo}
            className={"overlay overlay-oval"}
          />
        </div>
        <footer>
          <div className={messageClass}>{this.state.message}</div>
          <CaptureButtonPanel
            text="Start"
            onCaptureClick={this.start}
            disabled={!clrLoaded}
            backDisabled={!clrLoaded}
          />
        </footer>
      </PageContent>
    );
  }
}

export default connect((state) => state.livenessTest, {
  challengeTemplateUpload,
  setVideoId,
  retry,
  go,
  firstTimeInit,
  errorMessage,
})(PageLivenessTest);
